import { useAsyncCallback } from "../hooks/useAsyncCallback";
import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@components/ui/card";
import { Input } from "@components/ui/input";
import { Label } from "@components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@components/ui/empty";
import { Skeleton } from "@components/ui/skeleton";
import { PageHeader } from "@components/layout/PageHeader";
import { PageShell } from "@components/layout/PageShell";
import {
  createSlaRule,
  getSlaRules,
  updateSlaRule,
  type SlaRule,
} from "../lib/auth";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@components/ui/breadcrumb";
import { SidebarTrigger } from "@components/ui/sidebar";

type SlaRulesPageProps = {
  navigate: (route: string) => void;
};

const priorityOptions = ["low", "medium", "high", "critical"];
const noneOptionValue = "__none__";

const slaRuleFormSchema = z
  .object({
    name: z.string().trim().min(1, "Rule name is required."),
    keywords: z.string(),
    priority: z.string(),
    minAgeHours: z.string(),
    slaHours: z.string(),
  })
  .superRefine((values, ctx) => {
    const hasKeyword = values.keywords
      .split(",")
      .map((entry) => entry.trim())
      .some(Boolean);
    if (!hasKeyword) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["keywords"], message: "Add at least one keyword." });
    }
  });
type SlaRuleFormValues = z.infer<typeof slaRuleFormSchema>;

const slaRuleFormDefaults: SlaRuleFormValues = {
  name: "",
  keywords: "",
  priority: noneOptionValue,
  minAgeHours: "",
  slaHours: "",
};

export function SlaRulesPage({ navigate }: SlaRulesPageProps) {
  const [rules, setRules] = useState<SlaRule[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { register, control, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<SlaRuleFormValues>({
    resolver: zodResolver(slaRuleFormSchema),
    defaultValues: slaRuleFormDefaults,
  });

  const loadRules = () => {
    setIsLoading(true);
    setLoadError(null);
    return getSlaRules()
      .then((data) => setRules(data))
      .catch((err) => {
        const message =
          err instanceof Error ? err.message : "Unable to load SLA rules.";
        setLoadError(message);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    loadRules();
  }, []);

  const {
    pending: handleCreatePending,
    callback: handleCreate,
  } = useAsyncCallback(handleSubmit(async (values) => {
    setSubmitError(null);
    const keywordList = values.keywords.split(",").map((entry) => entry.trim()).filter(Boolean);
    try {
      await createSlaRule({
        name: values.name,
        keywords: keywordList,
        priority: values.priority === noneOptionValue ? undefined : values.priority,
        minAgeHours: values.minAgeHours ? Number(values.minAgeHours) : undefined,
        slaHours: values.slaHours ? Number(values.slaHours) : undefined,
      });
      reset(slaRuleFormDefaults);
      await loadRules();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Unable to create SLA rule.";
      setSubmitError(message);
    }
  }));

  const {
    pending: togglePending,
    activeArguments: toggleArguments,
    callback: toggle,
  } = useAsyncCallback(async (rule: SlaRule) => {
    try {
      await updateSlaRule(rule.id, { enabled: !rule.enabled });
      await loadRules();
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "Unable to update SLA rule.");
    }
  });

  const breadcrumb = (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <button type="button" onClick={() => navigate("/review")}>
              Review queue
            </button>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage>SLA rules</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );

  return (
    <PageShell maxWidth="6xl">
      <PageHeader
        breadcrumb={breadcrumb}
        leading={<SidebarTrigger />}
        eyebrow="SLA Prioritization"
        title="SLA Rules"
        description="Define keyword + age rules that set SLA targets and priority."
        actions={
          <>
            <Button variant="outline" onClick={() => navigate("/review")}>
              Back to review
            </Button>
          </>
        }
      />

      <section className="mt-8 space-y-6">
        {loadError ? (
          <p className="text-sm text-red-400" role="alert">
            {loadError}
          </p>
        ) : null}

        <Card className="border-border bg-card/60">
          <CardHeader>
            <CardTitle className="text-lg">Create SLA rule</CardTitle>
          </CardHeader>
          <form onSubmit={handleCreate} noValidate>
            <CardContent className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="sla-name">Rule name</Label>
              <Input
                id="sla-name"
                type="text"
                placeholder="Checkout outage"
                {...register("name")}
                aria-invalid={Boolean(errors.name)}
                aria-describedby={errors.name ? "sla-name-error" : undefined}
              />
              {errors.name ? (
                <p id="sla-name-error" role="alert" className="text-xs text-red-400">
                  {errors.name.message}
                </p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="sla-keywords">Keywords</Label>
              <Input
                id="sla-keywords"
                type="text"
                placeholder="checkout, outage, payment"
                {...register("keywords")}
                aria-invalid={Boolean(errors.keywords)}
                aria-describedby={errors.keywords ? "sla-keywords-error" : "sla-keywords-help"}
              />
              <p id="sla-keywords-help" className="text-xs text-muted-foreground/70">Comma-separated.</p>
              {errors.keywords ? (
                <p id="sla-keywords-error" role="alert" className="text-xs text-red-400">
                  {errors.keywords.message}
                </p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="sla-priority">Priority override</Label>
              <Controller
                name="priority"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="sla-priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={noneOptionValue}>No override</SelectItem>
                      {priorityOptions.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sla-min-age">Minimum age (hours)</Label>
              <Input
                id="sla-min-age"
                type="number"
                min="0"
                placeholder="0"
                {...register("minAgeHours")}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sla-hours">SLA target (hours)</Label>
              <Input
                id="sla-hours"
                type="number"
                min="1"
                placeholder="24"
                {...register("slaHours")}
              />
            </div>
            {submitError ? (
              <p className="md:col-span-2 text-sm text-red-400" role="alert">
                {submitError}
              </p>
            ) : null}
            <div className="md:col-span-2">
              <Button loading={handleCreatePending} loadingLabel="Creating SLA rule…" type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Creating..." : "Create SLA rule"}
              </Button>
            </div>
          </CardContent>
          </form>
        </Card>

        <Card className="border-border bg-card/60">
          <CardHeader>
            <CardTitle className="text-lg">Existing SLA rules</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, index) => (
                  <Card key={`sla-skeleton-${index}`} className="border-border bg-card/60">
                    <CardHeader className="space-y-2">
                      <Skeleton className="h-5 w-40" />
                      <Skeleton className="h-3 w-28" />
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-2/3" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : rules.length === 0 ? (
              <Empty className="border-border/60 bg-muted/40">
                <EmptyHeader>
                  <EmptyTitle>No SLA rules configured yet</EmptyTitle>
                  <EmptyDescription className="text-muted-foreground">
                    Define keywords and targets to surface SLA risk earlier.
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            ) : (
              rules.map((rule) => (
                <div
                  key={rule.id}
                  className="rounded-lg border border-border bg-muted/60 p-3 text-sm text-foreground/90"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold">{rule.name}</p>
                      <p className="text-xs text-muted-foreground/70">
                        {rule.keywords.join(", ") || "No keywords"}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      loading={togglePending && toggleArguments?.[0]?.id === rule.id}
                      loadingLabel="Updating rule…"
                      disabled={togglePending}
                      onClick={() => void toggle(rule)}
                    >
                      {rule.enabled ? "Disable" : "Enable"}
                    </Button>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
                    {rule.priority ? <span>Priority: {rule.priority}</span> : null}
                    {rule.minAgeHours !== undefined ? (
                      <span>Min age: {rule.minAgeHours}h</span>
                    ) : null}
                    {rule.slaHours !== undefined ? (
                      <span>SLA: {rule.slaHours}h</span>
                    ) : null}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </section>
    </PageShell>
  );
}
